import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, FileText } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line } from "recharts";
import { AdminTitle, DataTable, Stat } from "@/pages/admin/AdminLayout";
import { Spinner } from "@/components/site/Bits";
import { useApi, useDownload, fmtPrice } from "@/lib/api";

const ChartCard = ({ title, children }) => (
  <div className="card-lux !transform-none p-5"><h3 className="font-display font-bold mb-4">{title}</h3><div className="h-64" dir="ltr">{children}</div></div>
);

export default function Reports() {
  const api = useApi();
  const download = useDownload();
  const [r, setR] = useState(null);
  useEffect(() => {
    api("get", "/admin/reports").then(setR);
  }, [api]);
  if (!r) return <Spinner />;
  const ex = (u, n) => download(u, n).catch(() => toast.error("تعذر التصدير"));
  const chart = r.per_course.map((x) => ({ name: x.title.slice(0, 18), revenue: x.revenue, sold: x.seats_sold, left: x.seats_left }));
  return (
    <div data-testid="admin-reports">
      <AdminTitle title="التقارير">
        <button className="btn-gold-outline !py-2.5" onClick={() => ex("/admin/reports/export.xlsx", "report.xlsx")} data-testid="export-report-xlsx"><FileSpreadsheet size={16} />تصدير Excel</button>
        <button className="btn-copper !py-2.5" onClick={() => ex("/admin/reports/export.pdf", "report.pdf")} data-testid="export-report-pdf"><FileText size={16} />تصدير PDF</button>
      </AdminTitle>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat label="إجمالي الإيرادات" value={fmtPrice(r.total_revenue)} testId="report-revenue" />
        <Stat label="عدد المتدربين" value={r.students} testId="report-students" />
        <Stat label="تسجيلات آخر 30 يوماً" value={r.new_registrations_30d} testId="report-new" />
        <Stat label="مدفوعات ناجحة" value={r.payments_success} testId="report-success" />
        <Stat label="مدفوعات فاشلة" value={r.payments_failed} testId="report-failed" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6 [&>*]:min-w-0">
        <ChartCard title="المبيعات حسب الدورة (ر.ع.)">
          <ResponsiveContainer><BarChart data={chart}><CartesianGrid strokeDasharray="3 3" stroke="#E8DFD5" /><XAxis dataKey="name" fontSize={11} /><YAxis fontSize={11} /><Tooltip /><Bar dataKey="revenue" name="الإيراد" fill="#B87333" radius={[6, 6, 0, 0]} /></BarChart></ResponsiveContainer>
        </ChartCard>
        <ChartCard title="التسجيلات اليومية (30 يوماً)">
          <ResponsiveContainer><LineChart data={r.daily_registrations}><CartesianGrid strokeDasharray="3 3" stroke="#E8DFD5" /><XAxis dataKey="date" fontSize={11} /><YAxis allowDecimals={false} fontSize={11} /><Tooltip /><Line dataKey="count" name="تسجيلات" stroke="#D4AF37" strokeWidth={3} /></LineChart></ResponsiveContainer>
        </ChartCard>
      </div>
      <h2 className="font-display font-bold text-lg mt-8 mb-3">المبيعات والمقاعد والحضور حسب الدورة</h2>
      <DataTable head={["الدورة", "التسجيلات", "المبيعات", "الإيراد", "مقاعد مباعة", "مقاعد متبقية", "حضور", "غياب"]} testId="report-per-course-table">
        {r.per_course.map((x) => (
          <tr key={x.course_id}><td className="max-w-[260px] truncate">{x.title}</td><td>{x.registrations}</td><td>{x.sales}</td><td className="whitespace-nowrap">{fmtPrice(x.revenue)}</td><td>{x.seats_sold}</td><td>{x.seats_left}</td><td>{x.present}</td><td>{x.absent}</td></tr>
        ))}
      </DataTable>
      <h2 className="font-display font-bold text-lg mt-8 mb-3">الدورات الأكثر طلباً</h2>
      <ol className="card-lux !transform-none p-5 space-y-2 list-decimal pr-8" data-testid="report-top-courses">
        {r.top_courses.map((x) => <li key={x.course_id}>{x.title} — {x.registrations} تسجيل</li>)}
      </ol>
    </div>
  );
}
